import React from "react";
import PropTypes from "prop-types";
import {detect} from "detect-browser";

const browser = detect();

/**
 * Scrollable list of elements.
 * Specific elements can be scrolled to using refs and the scrollTargetID prop.
 * The key to this component is that it keeps a hash of refs to its children.
 * To ref children, pass a function as the `children` prop. That funciton takes a
 *   single argument which is a ref function. The ref function takes two arguments,
 *   a child element and an ID.
 */
export default class ScrollableList extends React.Component {

  static propTypes = {
    /**
     * Should the scrollTo'd elements be align to the bottom
     *   of the container instead of the top?
     */
    alignToBottom: PropTypes.bool,
    /**
     * The ID of the child element to scroll to.
     */
    scrollTargetID: PropTypes.string,
    /**
     * Behaves normally when passed a node (element/array/etc.).
     * When passed a function, uses FAC pattern called with a single arg
     *   which is a ref function taking (childElement, childID).
     */
    children: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.node
    ]).isRequired,

    /**
      * Should the component load more data on end of scroll
    */
    infiniteScroll: PropTypes.bool,

    /**
      * Function to run on end of scroll
    */
    loadMore: PropTypes.func,

  }

  static defaultProps = {
    reversed: false
  }

  container = React.createRef();

  /**
   * Map child IDs to their refs.
   * Populated by `refChild`.
   */
  childRefMap = {}

  /**
   * Store a ref to a child.
   * @param {node} child
   * @param {string} childID
   */
  refChild = (child, childID) => {
    this.childRefMap[childID] = child;
  }

  /**
   * Scroll to the child with the given ID.
   */
  scrollToChild(childID) {
    const container = this.container.current;
    const child = this.childRefMap[childID];
    if (container && child) {
      if (this.props.alignToBottom) {
        container.scrollTop = container.scrollHeight + child.offsetTop + child.offsetHeight - container.clientHeight * 2;
      } else {
        container.scrollTop = child.offsetTop;
      }
    }
  }

  /**
   * Scroll to the child specified by the `scrollTargetID` prop.
   * Do `requestAnimationFrame` to our `onScroll` callback (this ensures that
   *   the element has actually been displayed before we fire).
   */
  scrollToTarget() {
    const { scrollTargetID } = this.props;
    if (scrollTargetID) {
      this.scrollToChild(scrollTargetID);
      window.requestAnimationFrame(() => {
        this.props.onScroll();
      });
    }
  }

  /**
   * Get children from node or func.
   */
  getChildren() {
    let { children } = this.props;
    if (typeof children === "function") {
      children = children(this.refChild);
    }
    children = React.Children.toArray(children);
    return children;
  }

  componentDidMount() {
    this.scrollToTarget();
  }

  componentDidUpdate() {
    this.scrollToTarget();
    // Reset the scrolltop for safari
    if(!this.props.alignToBottom && (browser.name === "safari" || browser.name.indexOf("ios") > -1)){
      const messagesList = this.container.current.childNodes[0];
      messagesList.scrollTop = -(messagesList.scrollHeight - messagesList.offsetHeight) + 5;
    }
  }

  onScroll = () => {
    let messagesList = this.container.current.childNodes[0];
    if(this.props.infiniteScroll){
      // As scrollTop value gets reversed because of using `flex-direction: column-reverse` handle safari separately
      if(browser.name === "safari" || browser.name.indexOf("ios") > -1){
        if(messagesList.scrollHeight - messagesList.offsetHeight === -(messagesList.scrollTop)){
          this.props.loadMore();
        }
      }else{
        if(this.props.infiniteScroll && messagesList.scrollTop === 0){
          this.props.loadMore();
        }
      }
    }
  }

  render() {
    return (
      <div className={this.props.className} ref={this.container} onScroll={this.onScroll}>
        { this.getChildren() }
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/ScrollableList.js